import React, { useState } from 'react';
import './Contact.css';
import useSeo, { buildBreadcrumbs } from '../hooks/useSeo.js';
import apiClient from '../utils/apiClient.js';
import NotificationModal from '../components/NotificationModal.js';
import useNotification from '../hooks/useNotification.js';
import Spinner from '../components/Spinner.js';
import { serviceAreaList } from '../seo/serviceAreas.js';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Contact = () => {
  useSeo({
    title: "Contact Aeti Cleaning Services | Carpet Cleaning Perth",
    rawTitle: true,
    description:
      "Request a quote from Aeti Cleaning Services for carpet, rug, couch, upholstery, mattress, curtain, blind and window cleaning across Perth.",
    path: "/contact",
    structuredData: buildBreadcrumbs([
      { name: "Home", path: "/" },
      { name: "Contact", path: "/contact" },
    ]),
  });

  const [form, setForm] = useState({ name: '', email: '', message: '', website: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { notification, notify, dismiss } = useNotification();

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = 'Name is required';
    if (!EMAIL_REGEX.test(form.email.trim())) nextErrors.email = 'A valid email is required';
    if (!form.message.trim()) nextErrors.message = 'Message is required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await apiClient.post('/contact-messages', {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        website: form.website,
      });
      setForm({ name: '', email: '', message: '', website: '' });
      notify('success', 'Message sent', "Thanks for reaching out — we'll get back to you shortly.");
    } catch (err) {
      notify('error', 'Could not send message', err.response?.data?.error || 'Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-copy">
        <p className="eyebrow">Contact us</p>
        <h1>Let’s create a cleaner routine that fits your home.</h1>
        <p>
          Request a quote for professional carpet, rug, couch, upholstery, mattress, curtain, blind
          or window cleaning across Perth and surrounding suburbs. Complete the form below, call us
          or send us a message, and our team will respond as soon as possible.
        </p>
        <div className="contact-details">
          <p><strong>Aeti Cleaning Services</strong></p>
          <p><strong>Email:</strong> aeticleaningservices@gmail.com</p>
          <p><strong>Phone:</strong> <a href="tel:+61422519996">+61 422 519 996</a></p>
          <p><strong>Address:</strong> Lowth Road, Beckenham, WA 6107</p>
          <p><strong>Service Area:</strong> Perth and Surrounding Suburbs</p>
        </div>

        <div className="contact-service-areas">
          <p className="contact-service-areas-title">Suburbs we service</p>
          <p className="contact-service-areas-list">
            {serviceAreaList.map((area) => area.name).join(" · ")}
          </p>
          <p className="contact-service-areas-note">
            Don't see your suburb listed? Get in touch — we cover most of the greater Perth metro
            area and are always happy to check.
          </p>
        </div>
      </div>

      <form className="contact-form" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Your Name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        {errors.name && <p className="contact-form-error">{errors.name}</p>}

        <input
          type="email"
          placeholder="Your Email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        {errors.email && <p className="contact-form-error">{errors.email}</p>}

        <textarea
          placeholder="Your Message"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
        />
        {errors.message && <p className="contact-form-error">{errors.message}</p>}

        {/* Honeypot — hidden from real users, left empty by them; a filled value signals a bot. */}
        <div className="honeypot-field" aria-hidden="true">
          <label htmlFor="contact-website">Website</label>
          <input
            type="text"
            id="contact-website"
            name="website"
            tabIndex="-1"
            autoComplete="off"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
          />
        </div>

        <button type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <Spinner size="sm" inline /> Sending…
            </>
          ) : (
            'Send enquiry'
          )}
        </button>
      </form>

      {notification && (
        <NotificationModal
          variant={notification.variant}
          title={notification.title}
          message={notification.message}
          onClose={dismiss}
        />
      )}
    </div>
  );
};

export default Contact;
